import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { tStatic } from '@/i18n/I18nProvider';
import { getErrorMessage, getErrorStatus } from './api';

declare module '@tanstack/react-query' {
  interface Register {
    queryMeta: { silent?: boolean };
    mutationMeta: { silent?: boolean };
  }
}

/** One toast per distinct message within a short window (avoid floods when several queries fail together). */
const recent = new Map<string, number>();
function notifyError(error: unknown): void {
  const status = getErrorStatus(error);
  if (status === 401) return; // handled by the refresh interceptor / redirect to login
  let message: string;
  if (status === undefined) message = tStatic('common.errorNetwork');
  else if (status === 403) message = tStatic('common.errorForbidden');
  else message = getErrorMessage(error) ?? tStatic('common.errorGeneric');

  const now = Date.now();
  if ((recent.get(message) ?? 0) > now - 4000) return;
  recent.set(message, now);
  toast.error(message);
}

function shouldRetry(failureCount: number, error: unknown): boolean {
  const status = getErrorStatus(error);
  if (status !== undefined && status >= 400 && status < 500) return false;
  return failureCount < 2;
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (query.meta?.silent) return;
      notifyError(error);
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _vars, _ctx, mutation) => {
      if (mutation.meta?.silent || mutation.options.onError) return;
      notifyError(error);
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: shouldRetry,
      refetchOnWindowFocus: false,
    },
    mutations: { retry: false },
  },
});
